import React, { useState } from 'react';
import { AlertPreset, AlertRule } from '../../hooks/useAlertRules';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Bell, Smartphone, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { getIcon, getBadgeLabel, getBadgeStyle, getIconStyle, generateMeaningLine, AppNotification } from '../../hooks/useNotifications';
import { cn } from '../ui/Button';
interface AlertReviewPanelProps {
  preset: AlertPreset;
  rule: Partial<AlertRule>;
  onChange: (updates: Partial<AlertRule>) => void;
}
export function AlertReviewPanel({
  preset,
  rule,
  onChange
}: AlertReviewPanelProps) {
  const {
    workspaces
  } = useWorkspaces();
  const [nameError, setNameError] = useState(false);
  const mockNotification: AppNotification = {
    id: 'preview',
    alert_name: rule.alert_name || 'Untitled Alert',
    notification_type: preset.notification_type,
    event_type: 'triggered',
    severity: preset.category === 'Market signal' ? 'market' : preset.category === 'Model revision' ? 'critical' : 'model_update',
    sort_date: new Date().toISOString(),
    workspace_name: workspaces.find(w => w.id === rule.workspace_id)?.name || 'Select Workspace',
    has_state_details: true,
    is_superseded: false,
    signature_changed: false,
    read: false,
    details: rule.rule_details || {}
  };
  const Icon = getIcon(mockNotification.notification_type, mockNotification.details, mockNotification.severity);
  const badgeLabel = getBadgeLabel(mockNotification.event_type, mockNotification.notification_type);
  const badgeStyle = getBadgeStyle(mockNotification.event_type);
  const iconStyle = getIconStyle(mockNotification.notification_type, mockNotification.severity);
  const meaningLine = generateMeaningLine(mockNotification);
  return <div className="space-y-8" data-id="element-517">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-518">
        {/* Settings Form */}
        <div className="lg:col-span-2 space-y-6" data-id="element-519">
          <Card className="p-6 bg-slate-800/40 border-slate-700/50" data-id="element-520">
            <h3 className="text-lg font-semibold text-white mb-6" data-id="element-521">
              Finalize Alert
            </h3>

            <div className="space-y-6" data-id="element-522">
              {/* Name */}
              <div className="space-y-3" data-id="element-523">
                <label className="text-sm font-medium text-slate-300" data-id="element-524">
                  Alert Name <span className="text-red-400" data-id="element-525">*</span>
                </label>
                <Input value={rule.alert_name || ''} onChange={e => {
                onChange({
                  alert_name: e.target.value
                });
                if (e.target.value) setNameError(false);
              }} placeholder="e.g. High Wind Warning - North Sea" className={nameError ? 'border-red-500/50' : ''} data-id="element-526" />
                {nameError && <p className="text-xs text-red-400" data-id="element-527">Alert name is required</p>}
              </div>

              {/* Delivery */}
              <div className="pt-4 border-t border-slate-700/50 space-y-4" data-id="element-528">
                <label className="text-sm font-medium text-slate-300" data-id="element-529">
                  Delivery Channels
                </label>
                <div className="flex flex-col gap-3" data-id="element-530">
                  <label className="flex items-center justify-between p-3 rounded-xl bg-slate-900/50 border border-slate-700/50 cursor-pointer hover:border-slate-600 transition-colors" data-id="element-531">
                    <div className="flex items-center gap-3" data-id="element-532">
                      <div className="w-8 h-8 rounded-lg bg-pink-500/10 flex items-center justify-center text-pink-400" data-id="element-533">
                        <Bell size={16} data-id="element-534" />
                      </div>
                      <div data-id="element-535">
                        <div className="text-sm font-medium text-slate-200" data-id="element-536">
                          In-App Notifications
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-537">
                          Show in top bar and notification center
                        </div>
                      </div>
                    </div>
                    <div className="relative" data-id="element-538">
                      <input type="checkbox" checked={rule.delivery?.in_app} onChange={e => onChange({
                      delivery: {
                        ...rule.delivery!,
                        in_app: e.target.checked
                      }
                    })} className="peer sr-only" data-id="element-539" />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500" data-id="element-540"></div>
                    </div>
                  </label>

                  <label className="flex items-center justify-between p-3 rounded-xl bg-slate-900/50 border border-slate-700/50 cursor-pointer hover:border-slate-600 transition-colors" data-id="element-541">
                    <div className="flex items-center gap-3" data-id="element-542">
                      <div className="w-8 h-8 rounded-lg bg-pink-500/10 flex items-center justify-center text-pink-400" data-id="element-543">
                        <Smartphone size={16} data-id="element-544" />
                      </div>
                      <div data-id="element-545">
                        <div className="text-sm font-medium text-slate-200" data-id="element-546">
                          Push Notifications
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-547">
                          Browser and mobile push alerts
                        </div>
                      </div>
                    </div>
                    <div className="relative" data-id="element-548">
                      <input type="checkbox" checked={rule.delivery?.push} onChange={e => onChange({
                      delivery: {
                        ...rule.delivery!,
                        push: e.target.checked
                      }
                    })} className="peer sr-only" data-id="element-549" />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500" data-id="element-550"></div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="space-y-4" data-id="element-551">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-552">
            Live Preview
          </h3>
          <div className="relative" data-id="element-553">
            <div className={cn('group relative rounded-xl border transition-all duration-200 overflow-hidden bg-slate-800/60 border-slate-700 shadow-sm')} data-id="element-554">
              <div className="absolute left-0 top-3 bottom-3 w-[2px] rounded-full bg-white/[0.12]" data-id="element-555" />

              <div className="p-4 flex items-start gap-4" data-id="element-556">
                <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]', iconStyle.bg, iconStyle.color, iconStyle.border)} data-id="element-557">
                  <Icon size={18} data-id="element-558" />
                </div>

                <div className="flex-1 min-w-0" data-id="element-559">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5" data-id="element-560">
                    <h3 className="text-sm font-semibold text-white" data-id="element-561">
                      {mockNotification.alert_name}
                    </h3>
                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wider', badgeStyle.bg, badgeStyle.text, badgeStyle.border)} data-id="element-562">
                      {badgeLabel}
                    </span>
                    <span className="text-xs text-slate-500 ml-auto whitespace-nowrap font-mono" data-id="element-563">
                      Just now
                    </span>
                  </div>

                  <p className="text-sm leading-relaxed mb-2 text-slate-300" data-id="element-564">
                    {meaningLine}
                  </p>

                  <div className="flex items-center gap-3 text-[11px] text-slate-500" data-id="element-565">
                    {mockNotification.workspace_name && <span className="flex items-center gap-1.5" data-id="element-566">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-700" data-id="element-567" />
                        {mockNotification.workspace_name}
                      </span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800 text-xs text-slate-500" data-id="element-568">
              <p className="mb-2 font-medium text-slate-400" data-id="element-569">
                What happens next?
              </p>
              <ul className="list-disc pl-4 space-y-1" data-id="element-570">
                <li data-id="element-571">Alert will be active immediately</li>
                <li data-id="element-572">Notifications will appear in the selected workspace</li>
                <li data-id="element-573">You can edit or pause this rule later</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>;
}